import { useState } from 'react';
import clsx from 'clsx';
import Image from 'next/image';
import ReactGA from 'react-ga4';
import { useRouter } from 'next/router';
import { Box, Button, Container, Typography } from '@mui/material';
import { useStyles } from './styles';
import { useCustomServiceContext } from 'contexts/useCustomServiceContext';
import CustomButton from 'components/CustomButton';

const typesData = {
  graphical_abstract: {
    quantity: 1,
    express: false,
    total: 200,
  },
  presentation_design: {
    quantity: 1,
    express: false,
    total: 600,
  },
  cover_art: {
    quantity: 1,
    express: false,
    total: 350,
  },
  pharmaceutical_infographic: {
    quantity: 1,
    express: false,
    total: 1000,
  },
};

export default function TypeOfScience() {
  const classes = useStyles();
  const router = useRouter();
  const [customService, dispatchCustomService] = useCustomServiceContext();
  const [state, setState] = useState('infographics');

  function sendClickTrackingEvents(type) {
    ReactGA.event({
      category: 'landing_page',
      action: `click_request_type_${type}`,
      label: 'when users click on the editage banner',
    });
  }

  function handleButtonClick(type) {
    sendClickTrackingEvents(type);

    dispatchCustomService({
      ...customService,
      type,
      quantity: typesData[type].quantity,
      express: typesData[type].express,
      total: typesData[type].total,
    });

    router.push('/custom-services/purchase');
  }

  return (
    <Container>
      <Box py={8.75} px={10} id="select-type">
        <Box pb={6}>
          <Typography className={classes.title}>
            <span> What type of science design</span> are you looking for today?
          </Typography>
        </Box>

        <Box className={classes.switchBox} display="flex" justifyContent="start">
          <Button
            disableRipple
            onClick={() => setState('infographics')}
            className={clsx(classes.switch, state === 'infographics' && classes.activeSwitch)}
          >
            Infographics | Graphical Abstracts
          </Button>

          <Button
            disableRipple
            onClick={() => setState('presentation')}
            className={clsx(classes.switch, state === 'presentation' && classes.activeSwitch)}
          >
            Presentation Design
          </Button>

          <Button
            disableRipple
            onClick={() => setState('cover')}
            className={clsx(classes.switch, state === 'cover' && classes.activeSwitch)}
          >
            Cover Art
          </Button>

          <Button
            disableRipple
            onClick={() => setState('premium')}
            className={clsx(classes.switch, state === 'premium' && classes.activeSwitch)}
          >
            Premium Infographics
          </Button>
        </Box>

        <Box
          pt={8}
          className={clsx(classes.contentTransition, {
            [classes.contentHidden]: state !== 'infographics',
          })}
        >
          <Box display="flex" height={360} alignItems="center" justifyContent="center">
            <Box maxWidth={587}>
              <Typography className={classes.titleSection}>
                <span>Scientific journals, papers, books, press releases</span> and many others!
              </Typography>

              <Box py={3}>
                <Typography className={classes.descriptionSection}>
                  We create <span>custom visuals in Mind the Graph style</span> that align perfectly
                  with your unique scientific findings
                </Typography>

                <Typography className={classes.descriptionSection}>
                  <span>Each design is meticulously created</span> by professionals with a deep
                  understanding of science communication.
                </Typography>

                <Typography className={classes.descriptionSection}>
                  <span>Struggling to share your research on social media?</span> We translate your
                  findings into credible, engaging visuals for broader reach.
                </Typography>
              </Box>

              <Box display="flex" alignItems="center">
                <CustomButton onClick={() => handleButtonClick('graphical_abstract')}>
                  Request your design
                </CustomButton>

                <Typography className={classes.price}>
                  US$ <span>200</span>
                </Typography>
              </Box>
            </Box>

            <Image
              src="/images/type-of-science/infographics-section.webp"
              alt="infographics image"
              width={446}
              height={338}
              loading="lazy"
            />
          </Box>
        </Box>

        <Box
          className={clsx(classes.contentTransition, {
            [classes.contentHidden]: state !== 'presentation',
          })}
        >
          <Box display="flex" height={360} alignItems="center">
            <Box maxWidth={587}>
              <Typography className={classes.titleSection}>
                <span>Conferences, lectures or stakeholder meetings</span> presentations tailored
                for you and your audience
              </Typography>

              <Box py={3}>
                <Typography className={classes.descriptionSection}>
                  <span>We craft compelling narratives</span> that logically guides your audience
                  through your research and its implications.
                </Typography>

                <Typography className={classes.descriptionSection}>
                  <span>Superior graphics, diagrams, and images.</span> We ensure every visual
                  element is of the highest quality and accurately represents your data and
                  findings, adding credibility and appeal to your presentation.
                </Typography>
              </Box>

              <Box display="flex" alignItems="center">
                <CustomButton onClick={() => handleButtonClick('presentation_design')}>
                  Request your design
                </CustomButton>

                <Typography className={classes.price}>
                  US$ <span>600</span>
                </Typography>
              </Box>
            </Box>

            <Image
              src="/images/type-of-science/presentation-design-section.webp"
              alt="infographics image"
              width={446}
              height={354}
              loading="lazy"
            />
          </Box>
        </Box>

        <Box
          className={clsx(classes.contentTransition, {
            [classes.contentHidden]: state !== 'cover',
          })}
        >
          <Box display="flex" height={360} alignItems="center">
            <Box maxWidth={587}>
              <Typography className={classes.titleSection}>
                Showcase your research with high-impact. <span>Award-winning cover art</span> for
                scientific journals and beyond!
              </Typography>

              <Box py={3}>
                <Typography className={classes.descriptionSection}>
                  <span>Our unique methology</span> ensures your research gets the attention it
                  deserves
                </Typography>

                <Typography className={classes.descriptionSection}>
                  <span> Attention to details and design</span> is not just an aesthetic choice—it's
                  fundamental to building a solid reputation in the scientific community.
                </Typography>
              </Box>

              <Box display="flex" alignItems="center">
                <CustomButton onClick={() => handleButtonClick('cover_art')}>
                  Request your design
                </CustomButton>

                <Typography className={classes.price}>
                  US$ <span>350</span>
                </Typography>
              </Box>
            </Box>

            <Image
              src="/images/type-of-science/cover-section.webp"
              alt="infographics image"
              width={446}
              height={316}
              loading="lazy"
            />
          </Box>
        </Box>

        <Box
          className={clsx(classes.contentTransition, {
            [classes.contentHidden]: state !== 'premium',
          })}
        >
          <Box display="flex" height={360} alignItems="center">
            <Box maxWidth={587}>
              <Typography className={classes.titleSection}>
                Ideal for <span>Biotechs, Pharmaceutical companies</span> and Educational
                institutions
              </Typography>

              <Box py={3}>
                <Typography className={classes.descriptionSection}>
                  <span>Custom designs </span>created using external elements likes images, 3D
                  models and other data.
                </Typography>

                <Typography className={classes.descriptionSection}>
                  <span>We start from your scientific study</span> or technical briefing to ensure
                  an accurate representation of your information.
                </Typography>
              </Box>

              <Box display="flex" alignItems="center">
                <CustomButton onClick={() => handleButtonClick('pharmaceutical_infographic')}>
                  Request your design
                </CustomButton>

                <Typography className={classes.price}>
                  US$ <span>1000</span>
                </Typography>
              </Box>
            </Box>

            <Image
              src="/images/type-of-science/premium-section.webp"
              alt="infographics image"
              width={446}
              height={299}
              loading="lazy"
            />
          </Box>
        </Box>
      </Box>
    </Container>
  );
}
